import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';

/** A Venue's booking horizon (days) and cancellation cutoff (minutes), CONTEXT.md / ADR
 *  0048. A venue with no explicit override reads the defaults (90 days / 60 minutes);
 *  no enforcement of these values happens yet (#262). */
export interface VenueBookingSettings {
  venueId: string;
  bookingHorizonDays: number;
  cancellationCutoffMinutes: number;
}

export interface UpdateVenueBookingSettingsInput {
  bookingHorizonDays: number;
  cancellationCutoffMinutes: number;
}

const settingsKey = (venueId: string) => ['venues', venueId, 'booking-settings'] as const;

export const getVenueBookingSettings = (venueId: string): Promise<VenueBookingSettings> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/booking-settings', {
      params: { path: { venueId } },
    }),
  ) as Promise<VenueBookingSettings>;

export const updateVenueBookingSettings = (
  venueId: string,
  input: UpdateVenueBookingSettingsInput,
): Promise<VenueBookingSettings> =>
  unwrap(
    apiClient.PATCH('/api/v1/venue-workspace/venues/{venueId}/booking-settings', {
      params: { path: { venueId } },
      body: input,
    }),
  ) as Promise<VenueBookingSettings>;

export function useVenueBookingSettings(venueId: string | null) {
  return useQuery({
    queryKey: settingsKey(venueId ?? ''),
    queryFn: () => getVenueBookingSettings(venueId!),
    enabled: venueId !== null,
  });
}

export function useUpdateVenueBookingSettings(venueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateVenueBookingSettingsInput) =>
      updateVenueBookingSettings(venueId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settingsKey(venueId) }),
  });
}
